import React, { useState } from "react";
import { UploadSimple, Image as ImageIcon, X } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { api, API } from "../lib/kostra";
import { toast } from "sonner";

const CATEGORIES = [
  "Main Course", "Appetizer", "Dessert", "Beverage",
  "Bakery", "Snack", "Breakfast", "Salad", "Soup", "Side Dish",
];

export default function DishInfoSection({ recipe, onChange }) {
  const [uploading, setUploading] = useState(false);

  const set = (patch) => onChange({ ...recipe, ...patch });

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ photo_path: data.path });
      toast.success("Image uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const photoUrl = recipe.photo_path ? `${API}/files/${recipe.photo_path}` : null;

  return (
    <div className="kostra-card p-5 md:p-6" data-testid="dish-info-card">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div>
          <h3 className="kostra-section-title text-xl">Dish Information</h3>
          <p className="text-xs text-[#4B5563] mt-1">Core metadata for this recipe.</p>
        </div>
        <span className="gold-pill text-[11px] font-medium px-2.5 py-1 rounded-full">
          Step 1 of 4
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="kostra-data-label">Dish Name</Label>
              <Input
                value={recipe.dish_name || ""}
                onChange={(e) => set({ dish_name: e.target.value })}
                placeholder="e.g. Butter Chicken"
                className="mt-1"
                data-testid="dish-name-input"
              />
            </div>
            <div>
              <Label className="kostra-data-label">Category</Label>
              <Select
                value={recipe.category || "Main Course"}
                onValueChange={(v) => set({ category: v })}
              >
                <SelectTrigger className="mt-1" data-testid="dish-category-trigger">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`dish-category-${c.replace(/\s+/g, '-').toLowerCase()}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="kostra-data-label">Portion Size (g)</Label>
              <Input
                type="number"
                min="0"
                value={recipe.portion_size_g || 0}
                onChange={(e) => set({ portion_size_g: parseFloat(e.target.value) || 0 })}
                className="mt-1 font-mono-num"
                data-testid="portion-size-input"
              />
            </div>
            <div>
              <Label className="kostra-data-label">Number of Portions</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={recipe.num_portions || 1}
                onChange={(e) => set({ num_portions: parseInt(e.target.value, 10) || 1 })}
                className="mt-1 font-mono-num"
                data-testid="num-portions-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="kostra-data-label inline-flex items-center gap-1.5">
                Net Weight / Portion (g)
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[#FDF9EE] text-[#D4AF37] uppercase tracking-wider">
                  Production
                </span>
              </Label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={recipe.net_weight_per_portion_g || 0}
                onChange={(e) => set({ net_weight_per_portion_g: parseFloat(e.target.value) || 0 })}
                placeholder="e.g. 250"
                className="mt-1 font-mono-num"
                data-testid="net-weight-portion-input"
              />
              <p className="text-[11px] text-[#4B5563] mt-1">
                Used in Production Plan to compute cost / kg & cost / portion.
              </p>
            </div>
          </div>
          <div>
            <Label className="kostra-data-label">Notes</Label>
            <Textarea
              value={recipe.notes || ""}
              onChange={(e) => set({ notes: e.target.value })}
              rows={3}
              placeholder="Plating notes, allergens, variations..."
              className="mt-1"
              data-testid="dish-notes-input"
            />
          </div>
        </div>

        <div>
          <Label className="kostra-data-label">Dish Photo</Label>
          <div className="mt-1">
            {photoUrl ? (
              <div className="relative group">
                <img
                  src={photoUrl}
                  alt={recipe.dish_name || "Dish"}
                  className="w-full h-48 object-cover rounded-md border border-[#E5E7EB]"
                  data-testid="dish-photo-preview"
                />
                <button
                  type="button"
                  onClick={() => set({ photo_path: null })}
                  className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/95 border border-[#E5E7EB] inline-flex items-center justify-center hover:bg-[#FBE8E8] hover:text-[#DC2626] transition"
                  aria-label="Remove image"
                  data-testid="dish-photo-remove"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label
                htmlFor="dish-photo-input"
                className="flex flex-col items-center justify-center h-48 rounded-md border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] hover:border-[#0A3B24]/40 hover:bg-[#FDF9EE] cursor-pointer transition"
                data-testid="dish-photo-drop"
              >
                {uploading ? (
                  <span className="text-sm text-[#4B5563]">Uploading...</span>
                ) : (
                  <>
                    <UploadSimple size={28} weight="duotone" color="#0A3B24" />
                    <span className="mt-2 text-sm text-[#0A3B24] font-medium">Upload dish photo</span>
                    <span className="text-[11px] text-[#4B5563] mt-1">PNG / JPG / WebP · max 5 MB</span>
                  </>
                )}
              </label>
            )}
            <input
              id="dish-photo-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="dish-photo-input"
            />
          </div>
          {!photoUrl && (
            <p className="text-[11px] text-[#4B5563] mt-2 inline-flex items-center gap-1">
              <ImageIcon size={12} /> Embedded in PDF exports.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
